import {
	AlertTriangle,
	CheckCircle2,
	Loader2,
	Play,
	Plus,
	RefreshCw,
	Settings as SettingsIcon,
	XCircle,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { TUNABLES } from "../../shared/tunables";
import type { StoreDto } from "../../shared/types";
import { AddStoreModal } from "../components/AddStoreModal";
import { StoreSettingsModal } from "../components/StoreSettingsModal";
import { fmtInterval, fmtRelativeTime } from "../lib/format";
import { useTunables } from "../lib/tunables";
import { Button, HowItWorks, Page, Point, StateLabel, stateStripeClass, stateTextClass } from "../ui";
import { useWs } from "../ws";

type SweepUI =
	| { phase: "running"; pages: number; items: number; startedAt: number }
	| {
			phase: "done";
			ingested: number;
			problem: string | null;
			durationMs: number;
			finishedAt: number;
			expireAt: number;
	  }
	| { phase: "failed"; error: string; finishedAt: number; expireAt: number };

const TOAST_TTL_MS = 30_000;

// One template for the header and every row so the columns line up. Phones
// get name, status and gear only; from lg the note moves out from under the
// name into its own column, so every row stays one line tall.
const ROW_GRID =
	"grid-cols-[16px_minmax(0,1fr)_auto_auto] sm:grid-cols-[16px_minmax(9rem,1fr)_7rem_4.5rem_4rem_minmax(8rem,0.9fr)_6.75rem_2rem] lg:grid-cols-[16px_minmax(9rem,0.9fr)_7rem_4.5rem_4rem_minmax(8rem,0.8fr)_minmax(0,1.6fr)_6.75rem_2rem]";

async function fetchStores(): Promise<StoreDto[]> {
	const res = await fetch("/api/stores");
	const data = (await res.json()) as { stores: StoreDto[] };
	return data.stores;
}

async function patchStore(id: string, patch: { enabled?: boolean; config?: Record<string, unknown> }) {
	await fetch(`/api/stores/${encodeURIComponent(id)}`, {
		method: "PATCH",
		headers: { "content-type": "application/json" },
		body: JSON.stringify(patch),
	});
}

export function StoreSettings() {
	const { get } = useTunables();
	const [stores, setStores] = useState<StoreDto[]>([]);
	const [loading, setLoading] = useState(true);
	const [refreshing, setRefreshing] = useState(false);
	const [sweepState, setSweepState] = useState<Record<string, SweepUI>>({});
	const [openStoreId, setOpenStoreId] = useState<string | null>(null);
	const [addOpen, setAddOpen] = useState(false);
	const expiryTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

	const refresh = (initial = false) => {
		if (!initial) setRefreshing(true);
		return fetchStores()
			.then(setStores)
			.finally(() => {
				setLoading(false);
				setRefreshing(false);
			});
	};

	useEffect(() => {
		refresh(true);
	}, []);

	useEffect(() => {
		const timers = expiryTimers.current;
		return () => {
			for (const t of Object.values(timers)) clearTimeout(t);
		};
	}, []);

	const scheduleClear = (storeId: string) => {
		const existing = expiryTimers.current[storeId];
		if (existing) clearTimeout(existing);
		expiryTimers.current[storeId] = setTimeout(() => {
			setSweepState((prev) => {
				const next = { ...prev };
				delete next[storeId];
				return next;
			});
			delete expiryTimers.current[storeId];
		}, TOAST_TTL_MS);
	};

	useWs((msg) => {
		if (msg.type === "sweep.started") {
			setSweepState((prev) => ({
				...prev,
				[msg.storeId]: {
					phase: "running",
					pages: 0,
					items: 0,
					startedAt: msg.startedAt,
				},
			}));
		} else if (msg.type === "sweep.progress") {
			setSweepState((prev) => {
				const cur = prev[msg.storeId];
				if (!cur || cur.phase !== "running") return prev;
				return {
					...prev,
					[msg.storeId]: { ...cur, pages: msg.pages, items: msg.items },
				};
			});
		} else if (msg.type === "sweep.complete") {
			setSweepState((prev) => ({
				...prev,
				[msg.storeId]: {
					phase: "done",
					ingested: msg.ingested,
					problem: msg.problem,
					durationMs: msg.durationMs,
					finishedAt: Date.now(),
					expireAt: Date.now() + TOAST_TTL_MS,
				},
			}));
			scheduleClear(msg.storeId);
			refresh();
		} else if (msg.type === "sweep.failed") {
			setSweepState((prev) => ({
				...prev,
				[msg.storeId]: {
					phase: "failed",
					error: msg.error,
					finishedAt: Date.now(),
					expireAt: Date.now() + TOAST_TTL_MS,
				},
			}));
			scheduleClear(msg.storeId);
		}
	});

	const toggle = async (s: StoreDto) => {
		const next = !s.enabled;
		const needsTosAck = s.id === "cardcenter" && next && !s.config?.tosAcked;
		if (needsTosAck) {
			const ok = confirm(
				"Cardcenter's robots.txt explicitly forbids automated scraping and 'buy-for-me' agents. Enabling means you are knowingly going against their stated policy.\n\nContinue?",
			);
			if (!ok) return;
			await patchStore(s.id, {
				enabled: true,
				config: { ...s.config, tosAcked: true, tosAckedAt: Date.now() },
			});
		} else {
			await patchStore(s.id, { enabled: next });
		}
		refresh();
	};

	const sweepNow = async (id: string) => {
		setSweepState((prev) => ({
			...prev,
			[id]: { phase: "running", pages: 0, items: 0, startedAt: Date.now() },
		}));
		try {
			const res = await fetch(`/api/stores/${encodeURIComponent(id)}/sweep`, { method: "POST" });
			const data = (await res.json()) as { ok?: boolean; error?: string };
			if (!data.ok) {
				setSweepState((prev) => ({
					...prev,
					[id]: {
						phase: "failed",
						error: data.error ?? "unknown",
						finishedAt: Date.now(),
						expireAt: Date.now() + TOAST_TTL_MS,
					},
				}));
				scheduleClear(id);
			}
		} catch (e) {
			setSweepState((prev) => ({
				...prev,
				[id]: {
					phase: "failed",
					error: (e as Error).message,
					finishedAt: Date.now(),
					expireAt: Date.now() + TOAST_TTL_MS,
				},
			}));
			scheduleClear(id);
		}
	};

	const openStore = stores.find((s) => s.id === openStoreId) ?? null;
	const totalItems = stores.reduce((acc, s) => acc + s.itemCount, 0);

	return (
		<Page
			title="Stores"
			help={
				<HowItWorks>
					<Point lead="Sweeps.">
						A sweep reads a store's product listings page by page and records the price and stock of every product it
						sees. That is how new products, restocks and price changes are found. Each store is swept on its own
						cadence, every {get(TUNABLES.sweepEvery)} minutes unless you change it, and Sweep now runs one straight
						away.
					</Point>
					<Point lead="A new store starts quietly.">
						Its first complete sweep learns the catalogue without sending any alerts, so adding a store doesn't flood
						the feed.
					</Point>
					<Point lead="The 50-page limit.">
						A sweep stops after {get(TUNABLES.sweepMaxPages)} pages, about{" "}
						{(get(TUNABLES.sweepMaxPages) * 100).toLocaleString("en")} products. A ⚠ next to Items means the last sweep
						was cut off there. Some stores carry on from where it stopped next time; others start over and never reach
						the end. Pick categories to keep a large store under the limit, or raise it under{" "}
						<a href="#/manage/settings" className="text-ink underline">
							Settings
						</a>
						.
					</Point>
					<Point lead="Categories.">
						With none picked, the whole catalogue is swept. Picking categories sweeps only those, and takes products
						outside them out of Items. They come back if a later sweep sees them again.
					</Point>
					<Point lead="Products that disappear">
						from a complete sweep are marked out of stock. That never sends an alert.
					</Point>
					<Point lead="Turning a store off">
						stops its sweeps. Products you watch at that store are still checked.
					</Point>
					<Point lead="Stack">is the shop platform the store runs on, which decides how it is read.</Point>
				</HowItWorks>
			}
			count={
				<span title={`${stores.length} stores · ${totalItems.toLocaleString()} items tracked`}>
					{stores.length} stores
				</span>
			}
			subtitle="The shops being read, how often, and which of their categories count. Click a store to change its settings."
			actions={
				<>
					<Button variant="primary" size="sm" onClick={() => setAddOpen(true)}>
						<Plus className="size-3.5" aria-hidden />
						Add store
					</Button>
					<Button variant="secondary" size="sm" onClick={() => refresh()} disabled={refreshing}>
						{refreshing ? (
							<Loader2 className="size-3.5 animate-spin" aria-hidden />
						) : (
							<RefreshCw className="size-3.5" aria-hidden />
						)}
						Refresh
					</Button>
				</>
			}
		>
			{loading ? (
				<p className="text-[13px] text-muted">Loading…</p>
			) : (
				<div className="overflow-hidden rounded-[10px] border border-hairline bg-paper">
					<div
						className={`hidden ${ROW_GRID} items-center gap-x-3 border-b border-hairline px-3 py-1.5 text-[11px] font-medium text-muted sm:grid`}
					>
						<span aria-hidden />
						<span>Store</span>
						<span title="The shop platform the store runs on">Stack</span>
						<span className="text-right" title="Products currently listed by this store">
							Items
						</span>
						<span className="text-right" title="How often the store is swept">
							Cadence
						</span>
						<span title="When the last sweep finished">Last sweep</span>
						<span className="hidden lg:block">Note</span>
						<span title="Off stops sweeps; watched products are still checked">Status</span>
						<span aria-hidden />
					</div>
					<ul className="divide-y divide-hairline">
						{stores.map((s) => (
							<StoreRow
								key={s.id}
								store={s}
								sweep={sweepState[s.id]}
								onOpen={() => setOpenStoreId(s.id)}
								onToggle={() => toggle(s)}
								onSweepNow={() => sweepNow(s.id)}
							/>
						))}
					</ul>
				</div>
			)}

			<StoreSettingsModal
				store={openStore}
				open={openStore != null}
				onClose={() => setOpenStoreId(null)}
				onSaved={() => refresh()}
			/>

			<AddStoreModal
				open={addOpen}
				onClose={() => setAddOpen(false)}
				onCreated={(store) => {
					setStores((prev) => (prev.some((s) => s.id === store.id) ? prev : [...prev, store]));
					setAddOpen(false);
				}}
			/>
		</Page>
	);
}

function StoreRow({
	store,
	sweep,
	onOpen,
	onToggle,
	onSweepNow,
}: {
	store: StoreDto;
	sweep: SweepUI | undefined;
	onOpen: () => void;
	onToggle: () => void;
	onSweepNow: () => void;
}) {
	const stack = String(store.config?.stack ?? "—");
	const { get } = useTunables();
	const defaultMs = get(TUNABLES.sweepEvery) * 60_000;
	const intervalMs = Number(store.config?.sweepIntervalMs ?? defaultMs);
	const customCadence = typeof store.config?.sweepIntervalMs === "number" && store.config.sweepIntervalMs !== defaultMs;
	const note = typeof store.config?.note === "string" ? store.config.note : null;
	const running = sweep?.phase === "running";

	// The dot is enabled/disabled by default (words carrying that state live
	// in the toggle button and note text elsewhere in the row), but a failed
	// sweep overrides it with the fault hue so a broken store is visible at
	// every width, not just sm:+ where SweepCell's text renders. The title
	// always matches exactly what the dot is currently encoding.
	const failedSweep = sweep?.phase === "failed" ? sweep : null;
	const dotClass = failedSweep ? stateStripeClass("fault") : store.enabled ? "bg-ink" : "bg-hairline";
	const dotTitle = failedSweep ? "sweep failed" : store.enabled ? "enabled" : "disabled";

	return (
		<li
			className={`group grid cursor-pointer ${ROW_GRID} items-center gap-x-3 px-3 py-1 transition-colors hover:bg-raised`}
			onClick={(e) => {
				const target = e.target as HTMLElement;
				if (target.closest("button")) return;
				onOpen();
			}}
		>
			<span className="flex justify-center" aria-hidden>
				<span className={`size-2 rounded-full ${dotClass}`} title={dotTitle} />
			</span>
			<div className="min-w-0 py-0.5">
				<div className="flex items-center gap-2">
					<span className="truncate text-[13px] font-medium text-ink">{store.name}</span>
					<span className="rounded-full bg-raised px-2 py-0.5 text-[11px] font-medium text-muted sm:hidden">
						{stack}
					</span>
				</div>
				{/* The host is shown in the settings modal's header, where there is
            room for it. Repeating it under every name cost a line per row and
            told you nothing the name did not. From lg the note gets its own
            column, so every row stays one line tall. */}
				{note && (
					<div className="lg:hidden">
						<StoreNote note={note} />
					</div>
				)}
				{/* Mobile-only: SweepCell's failure text is desktop-only
            (hidden sm:block, below), and the dot alone can't carry state
            per colour-never-alone — so a failed sweep needs its own word
            here too. Lives in this 1fr column rather than widening the
            mobile grid template, so it can't disturb the toggle/gear
            layout or overflow. */}
				{failedSweep && (
					<div className="mt-0.5 sm:hidden">
						<StateLabel state="fault" label={`Sweep failed · ${fmtRelativeTime(failedSweep.finishedAt)}`} />
					</div>
				)}
			</div>
			<span className="hidden truncate text-xs text-muted sm:inline">{stack}</span>
			<span
				className="hidden text-right text-[13px] font-medium font-tag text-ink sm:inline"
				title={`${store.itemCount} active products`}
			>
				{store.itemCount.toLocaleString()}
				{store.lastSweepTruncated && (
					<span
						className={`ml-1 ${stateTextClass("fault")}`}
						title="The last sweep stopped at the page limit, so only part of this store was refreshed. Narrow it with a category filter."
					>
						⚠
					</span>
				)}
			</span>
			<span
				className="hidden text-right text-xs sm:inline"
				title={`every ${fmtInterval(intervalMs)}${customCadence ? "" : " (default)"}`}
			>
				<span className={customCadence ? "font-semibold text-ink" : "text-muted"}>{fmtInterval(intervalMs)}</span>
			</span>
			<span className="hidden truncate text-xs sm:block">
				<SweepCell sweep={sweep} lastSweepAt={store.lastSweepAt} />
			</span>
			<span className="hidden min-w-0 lg:block">{note && <StoreNote note={note} />}</span>
			<span className="flex items-center justify-start gap-1">
				<button
					onClick={onSweepNow}
					disabled={!store.enabled || running}
					className="hidden size-tap items-center justify-center rounded-md text-muted group-hover:inline-flex hover:bg-paper hover:text-ink disabled:opacity-30 sm:inline-flex"
					aria-label="Sweep now"
					title="Sweep now"
				>
					{running ? (
						<Loader2 className="size-3.5 animate-spin" aria-hidden />
					) : (
						<Play className="size-3.5" aria-hidden />
					)}
				</button>
				<button
					onClick={onToggle}
					className={`min-h-tap min-w-[4.5rem] rounded-md border px-2 text-xs font-medium transition-colors ${
						store.enabled
							? "border-line bg-raised text-ink hover:bg-ground"
							: "border-hairline text-muted hover:border-line hover:text-ink"
					}`}
					title="Toggle enabled"
				>
					{store.enabled ? "Enabled" : "Disabled"}
				</button>
			</span>
			<button
				onClick={onOpen}
				className="flex size-tap items-center justify-center rounded-md text-muted hover:bg-paper hover:text-ink"
				aria-label="Open store settings"
				title="Settings"
			>
				<SettingsIcon className="size-4" aria-hidden />
			</button>
			{/* Why the last sweep came up short, on a line of its own so it can be
          read in full: the reason is the whole point, and a truncated one in
          a table cell is how "0 ingested" went unexplained. Persisted on the
          server, so it stays until a sweep comes back clean. */}
			{store.lastSweepProblem && (
				<p
					className={`col-span-full flex items-start gap-1.5 pb-1.5 text-xs sm:col-start-2 ${stateTextClass("needs")}`}
				>
					<AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden />
					<span>
						<span className="font-semibold">Last sweep came up short.</span> {store.lastSweepProblem}
					</span>
				</p>
			)}
		</li>
	);
}

/** A store's warning note, in the needs hue with its triangle. */
function StoreNote({ note }: { note: string }) {
	return (
		<div className={`flex min-w-0 items-center gap-1 text-[11px] ${stateTextClass("needs")}`} title={note}>
			<AlertTriangle className="size-3 shrink-0" aria-hidden />
			<span className="truncate">{note}</span>
		</div>
	);
}

function SweepCell({ sweep, lastSweepAt }: { sweep: SweepUI | undefined; lastSweepAt: number | null }) {
	if (!sweep) {
		// The server only remembers this since it last started, so a store not
		// swept since then has nothing to show yet.
		return lastSweepAt ? (
			<span className="text-muted">{fmtRelativeTime(lastSweepAt)}</span>
		) : (
			<span className="text-muted" title="Not swept since the server started">
				—
			</span>
		);
	}
	if (sweep.phase === "running") {
		return (
			<span className="inline-flex items-center gap-1.5 text-ink">
				<Loader2 className="size-3 animate-spin" aria-hidden />
				sweeping… {sweep.pages}p · {sweep.items}i
			</span>
		);
	}
	if (sweep.phase === "done") {
		if (sweep.problem) {
			return (
				<span className={`inline-flex items-center gap-1.5 ${stateTextClass("needs")}`} title={sweep.problem}>
					<AlertTriangle className="size-3" aria-hidden />
					<span className="truncate">
						{sweep.ingested} ingested · {fmtRelativeTime(sweep.finishedAt)}
					</span>
				</span>
			);
		}
		return (
			<span className="inline-flex items-center gap-1.5 text-ink">
				<CheckCircle2 className="size-3" aria-hidden />
				<span className="truncate">
					{sweep.ingested} ingested · {fmtRelativeTime(sweep.finishedAt)}
				</span>
			</span>
		);
	}
	return (
		<span className="inline-flex items-center gap-1.5" title={sweep.error}>
			<XCircle className="size-3 text-muted" aria-hidden />
			<StateLabel state="fault" label={`Failed · ${fmtRelativeTime(sweep.finishedAt)}`} />
		</span>
	);
}
